/**
 * Deterministic hashing and pseudo-random generation.
 *
 * Ported from the prototype's FNV-1a + xorshift32 pair. Determinism is a
 * requirement, not a convenience: the demo datasets, the per-report executive
 * summaries and the partner-by-channel confidence matrix are all generated,
 * and they must be identical across reloads so a screenshot in a review still
 * matches the screen a day later.
 */

/** FNV-1a over the string, normalised to [0, 1). */
export function seedFrom(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 0xffffffff;
}

/**
 * xorshift32 generator seeded from a string.
 *
 * Returns a closure producing successive values in [0, 1). Callers that need
 * reproducibility must create the generator from a stable seed string and
 * consume it in a stable order.
 */
export function rng(seed: string): () => number {
  let s = Math.floor(seedFrom(seed) * 4294967295) || 123456789;
  return () => {
    s ^= s << 13;
    s >>>= 0;
    s ^= s >> 17;
    s ^= s << 5;
    s >>>= 0;
    return s / 4294967296;
  };
}

/** Monotonic id generator, scoped per prefix. Session-local only. */
const counters = new Map<string, number>();

export function uid(prefix = 'id'): string {
  const next = (counters.get(prefix) ?? 0) + 1;
  counters.set(prefix, next);
  return `${prefix}-${next}`;
}

/** Test seam: resets the uid counters so ids are predictable per test. */
export function resetUid(): void {
  counters.clear();
}
